import { useEffect, useReducer, useState } from 'react';

import {
  Row,
  Col,
  Typography,
  Skeleton,
  Tabs,
  Badge,
  Space,
  Divider,
  Popconfirm,
  Modal,
  Input,
  Form,
  notification,
  Alert,
} from 'antd';

import CustomSelector from '../../components/CustomSelector';

import { CustomButton, CustomTooltip } from '../../components';
import MESSAGES from '../../enums/Messages';
import { Link } from 'react-router-dom';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import AjaxTable from '../../components/AjaxTable';
import Paragraph from 'antd/lib/typography/Paragraph';
import {
  getCounters,
  getProjects,
  getRoles,
  ajaxTable as filterAjaxTable,
  annulate,
  sendUnilateral,
  getPriorities,
} from '../../api/module/pc_followup';

import '../../styles/main.scss';
import {
  catchExceptionSRate,
  useParsedSearch,
} from '../../utils/helpers/helpers';
import BetterUpload from '../../components/BetterUpload';
import { EyeOutlined } from '@ant-design/icons';
import { generateTemplateURL } from '../../api/module/template';
import PdfViewer from '../../components/PdfViewer';

const { Title } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const breadcrumbItems = [
  {
    text: 'Seguimiento de actas de terminación y liquidación',
  },
];

const initSelectors = {
  proyecto: undefined,
  cargo: undefined,
};

function reducer(state, action) {
  switch (action.type) {
    case 'project':
      return { ...initSelectors, proyecto: action.payload };
    case 'priority':
      return {
        ...initSelectors,
        proyecto: state.proyecto,
        prioridad: action.payload,
      };
    case 'role':
      return {
        ...state,
        cargo: action.payload,
      };

    default:
      return initSelectors;
  }
}

function PCFollowUpSelectors({ setQuery }) {
  const [state, dispatch] = useReducer(reducer, initSelectors);
  const searchParams = useParsedSearch({
    proyecto: 'int',
    prioridad: ['Vencido', 'Crítico', 'Prioritario', 'En tiempo'],
  });

  useEffect(() => {
    if (
      (!searchParams.proyecto && !searchParams.prioridad) ||
      Object.values(state).some((v) => !!v)
    ) {
      let q = '';
      if (q === '' && state.cargo) {
        q = `filter[prioridad]=${state.cargo}&filter[cargo]=${state.cargo}`;
      }
      if (q === '' && state.prioridad) {
        q = `filter[proyecto]=${state.proyecto}&filter[prioridad]=${state.prioridad}`;
      }
      if (q === '' && state.proyecto) {
        q = `filter[proyecto]=${state.proyecto}`;
      }
      setQuery({ query: q, param: state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(state)]);

  return (
    <div className="box-description" key="sa-selectors">
      <Alert
        showIcon
        type="info"
        description="Tenga en cuenta que en este módulo se pueden gestionar las actas de terminación BILATERALES que NO han sido aceptadas o han sido rechazadas por los (las) contratistas"
      />
      <Row className="box-selectors">
        <Col span={6}>
          <Title level={4}>Proyecto a revisar:</Title>
        </Col>
        <Col span={17} offset={0.5}>
          <CustomSelector
            onChange={(value) => dispatch({ type: 'project', payload: value })}
            initialValue={searchParams.proyecto}
            onLoad={(value) => {
              if (searchParams.proyecto)
                dispatch({
                  type: 'project',
                  payload: value ? value.value : undefined,
                });
            }}
            selectorProps={{
              style: { width: '100%' },
              placeholder: 'Seleccione el proyecto a revisar...',
            }}
            endpointFetch={async () => {
              return await getProjects();
            }}
            key={searchParams.proyecto || 'py'}
          />
        </Col>
      </Row>
      {state.proyecto && (
        <Row className="box-selectors">
          <Col span={6}>
            <Title level={4}>Estado de priorización:</Title>
          </Col>
          <Col span={17} offset={0.5}>
            <CustomSelector
              onChange={(value) =>
                dispatch({ type: 'priority', payload: value })
              }
              initialValue={searchParams.prioridad}
              onLoad={(value) => {
                if (searchParams.prioridad)
                  dispatch({
                    type: 'priority',
                    payload: value ? value.value : undefined,
                  });
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder: 'Seleccione la prioridad...',
              }}
              endpointFetch={async () => {
                return await getPriorities({
                  'filter[proyecto]': state.proyecto,
                });
              }}
              key={`rs-${state.proyecto}`}
            />
          </Col>
        </Row>
      )}
      {state.prioridad && (
        <Row className="box-selectors">
          <Col span={6}>
            <Title level={4}>Cargo/rol a revisar:</Title>
          </Col>
          <Col span={17} offset={0.5}>
            <CustomSelector
              onChange={(value) => dispatch({ type: 'role', payload: value })}
              initialValue={searchParams.cargo}
              onLoad={(value) => {
                if (searchParams.cargo)
                  dispatch({
                    type: 'role',
                    payload: value ? value.value : undefined,
                  });
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder: 'Seleccione el Cargo / Rol a revisar... ',
              }}
              endpointFetch={async () => {
                return await getRoles({
                  'filter[proyecto]': state.proyecto,
                  'filter[prioridad]': state.prioridad,
                });
              }}
              key={`rs-${state.prioridad}`}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

function AnnulationModal({ record, onFinish, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  async function onSelfFinish(values) {
    setLoading(true);
    try {
      const formData = new FormData();
      for (const prop in values)
        if (values[prop]) formData.append(prop, values[prop]);
      await annulate(record.id, formData);
      onFinish(record.id);
    } catch (error) {
      catchExceptionSRate(error);
      setLoading(false);
    }
  }

  if (!record) return null;

  return (
    <Modal
      centered
      title={<Title level={4}>Anulación</Title>}
      closable={false}
      open={record?.id}
      footer={
        <>
          <CustomButton
            type="default"
            htmlType="button"
            text="Cancelar"
            onClick={onCancel}
          />
          <CustomButton
            type="danger"
            text="Anular"
            htmlType="button"
            loading={loading}
            onClick={() => {
              form.submit();
            }}
          />
        </>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onSelfFinish}>
        <Form.Item
          label="Justifique la anulación de esta acta"
          name="justificacion_anulacion"
          rules={[{ required: true, message: 'Ingrese una justificación' }]}
        >
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item
          label="En caso de tener soporte de la anulación del acta, por favor cárguelo"
          name="soporte_anulacion"
        >
          <BetterUpload fileType="custom" accept="application/pdf,image/*" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function PostContractualFollowUp() {
  const [query, setQuery] = useState({ q: '', param: {} });
  const [counters, setCounters] = useState(undefined);
  const [activeTab, setActiveTab] = useState('review');

  const [annulateRecord, setAR] = useState(undefined);

  const [toUnilateral, setToUnilateral] = useState(undefined);

  const [reloadSource, setReloadSource] = useState(false);

  const [pdfUrl, setPDFUrl] = useState(undefined);
  const [loadingPDF, setLoadingPDF] = useState(undefined);

  async function changeQuery(
    q = { query: '', param: undefined },
    newTab = undefined,
  ) {
    try {
      const res = await getCounters(`/?${q.query}`);
      if (q !== query) setQuery(q);
      setCounters(res.data.results);
      if (newTab) setActiveTab(newTab);
    } catch (err) {
      catchExceptionSRate(err);
      setCounters(undefined);
    }
  }

  async function onSendUnilateral(record) {
    try {
      setToUnilateral(record.id);
      await sendUnilateral(record.id);
      setToUnilateral();
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      const res = await getCounters(`/?${query.query}`);
      setCounters(res.data.results);
      setReloadSource({});
    } catch (err) {
      setToUnilateral();
      console.log(err);
      catchExceptionSRate(err, MESSAGES.DESC_ERROR_POST_MSG);
    }
  }

  async function onAfterAnnulation() {
    notification.success({
      message: MESSAGES.SUCCESS_MSG,
      description: MESSAGES.DESC_SUCCESS_MSG,
      duration: 2,
    });
    const res = await getCounters(`/?${query.query}`);
    setCounters(res.data.results);
    setReloadSource({});
    setAR();
  }

  const getURLPDF = async (record) => {
    try {
      setLoadingPDF(record.id);
      const response = await generateTemplateURL({
        person: record.tercero,
        term: record.termino,
        contract: record.contrato_id,
        postcontractual: record.id,
        template_id: record.plantilla,
      });
      setPDFUrl(response.data.url);
      setLoadingPDF(null);
    } catch (err) {
      setLoadingPDF(null);
      console.log(err);
    }
  };

  const columns = [
    {
      title: 'Proyecto',
      dataIndex: 'proyecto_nombre',
    },
    {
      title: 'Cargo / Rol',
      dataIndex: 'cargo_nombre',
    },
    {
      title: 'Contratista',
      dataIndex: 'contratista_nombre',
    },
    {
      title: 'No. Contrato',
      dataIndex: 'contrato_num',
    },
    {
      title: 'Fecha efectiva',
      dataIndex: 'fecha_fin',
      align: 'center',
      width: '8em',
      hidden: activeTab !== 'review',
    },
    {
      title: 'Fecha envío acta',
      dataIndex: 'fecha_envio',
      align: 'center',
      width: '8em',
    },
    {
      title: 'Fecha rechazo',
      dataIndex: 'fecha_rechazo',
      align: 'center',
      width: '8em',
      hidden: activeTab === 'review',
    },
    {
      title: (
        <>
          Prioridad
          <CustomTooltip
            title={
              <ul style={{ paddingLeft: '1.5em', marginBottom: 0 }}>
                <li>Vencido: -n a 0</li>
                <li>Crítico: 1-5</li>
                <li>Prioritario: 6-30</li>
                <li>En tiempo: 31 o mas</li>
              </ul>
            }
          />
        </>
      ),
      dataIndex: 'prioridad',
      align: 'center',
      hidden: activeTab !== 'review',
    },
    {
      title: (
        <>
          Días para firma
          <CustomTooltip
            title={
              <ul style={{ paddingLeft: '1.5em', marginBottom: 0 }}>
                <li>Vencido: -n a 0</li>
                <li>Crítico: 1-5</li>
                <li>Prioritario: 6-30</li>
                <li>En tiempo: 31 o mas</li>
              </ul>
            }
          />
        </>
      ),
      dataIndex: 'dias_firma',
      align: 'center',
      width: '7em',
      render: (t, record) => (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor:
              record.prioridad === 'Vencido'
                ? '#990000'
                : record.prioridad === 'Crítico'
                  ? '#F0505F'
                  : record.prioridad === 'Prioritario'
                    ? '#FFD800'
                    : '#A0C864',
            color:
              record.prioridad === 'Vencido' || record.prioridad === 'Crítico'
                ? 'white'
                : 'black',
            textAlign: 'center',
            padding: 4,
            fontWeight: 'bold',
          }}
        >
          {t}
        </div>
      ),
    },
    {
      title: 'Tipo acta bilateral',
      dataIndex: 'tipo_nombre',
      width: '8em',
      align: 'center',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: '10em',
      align: 'center',
      render: (_, record) => {
        return (
          <Space split={<Divider type="vertical" />}>
            <CustomButton
              type="primary"
              htmlType="button"
              onClick={() => {
                getURLPDF(record);
              }}
              text={
                <>
                  <EyeOutlined /> Bilateral
                </>
              }
              loading={loadingPDF === record.id}
              disabled={!!toUnilateral}
            />
            {activeTab !== 'review' ? (
              <Link
                to={`${INTERNAL_LINKS.POSTCONTRACTUAL}-form-followup/${record.id}`}
              >
                <CustomButton
                  type="primary"
                  text="Editar"
                  disabled={!!toUnilateral}
                />
              </Link>
            ) : null}
            <Popconfirm
              onConfirm={() => {
                onSendUnilateral(record);
              }}
              title={
                <p>
                  ¿Desea cambiar el acta <u>{record.tipo_nombre}</u> a una
                  unilateral?
                </p>
              }
            >
              <CustomButton
                type="primary"
                text="Enviar Unilateral"
                loading={toUnilateral === record.id}
                disabled={!!toUnilateral}
                className="background-bigaro"
              />
            </Popconfirm>
            <CustomButton
              type="danger"
              text="Anular"
              onClick={() => setAR(record)}
              disabled={!!annulateRecord?.id}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Seguimiento de actas de terminación y liquidación
      </Title>
      <PCFollowUpSelectors setQuery={changeQuery} />

      {counters ? (
        <>
          <Tabs
            style={{ marginTop: 15 }}
            defaultActiveKey={'review'}
            activeKey={activeTab}
            onTabClick={(key, _) => {
              setActiveTab(key);
            }}
            destroyInactiveTabPane={true}
            type="card"
            key={`spc-home-tabs-${query.query}`}
          >
            <TabPane
              tab={
                <Paragraph>
                  Por revisar
                  <Badge showZero count={counters.pending} />
                </Paragraph>
              }
              key="review"
            >
              <AjaxTable
                columns={columns}
                endpoint={filterAjaxTable}
                query={`/?${query.query}&filter[r]=0`}
                reloadSource={reloadSource}
                searchInput
              />
            </TabPane>
            <TabPane
              tab={
                <Paragraph>
                  Rechazos actas
                  <Badge showZero count={counters.rejected} />
                </Paragraph>
              }
              key="rejected"
            >
              <AjaxTable
                columns={columns}
                endpoint={filterAjaxTable}
                query={`/?${query.query}&filter[r]=1`}
                reloadSource={reloadSource}
                searchInput
              />
            </TabPane>
          </Tabs>
        </>
      ) : (
        <Skeleton active loading={true} />
      )}
      <AnnulationModal
        onFinish={onAfterAnnulation}
        onCancel={() => setAR()}
        record={annulateRecord}
        key={annulateRecord?.id || 'no-key'}
      />
      <PdfViewer
        pdf={pdfUrl}
        onCancel={() => setPDFUrl(undefined)}
        open={pdfUrl}
        isWord={true}
      />
    </>
  );
}
